import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useJsApiLoader } from '@react-google-maps/api';
import LampControl from '../components/LampControl';
import { useLocation } from 'react-router-dom';
import axios from "axios";
import mqtt from 'mqtt';

const GoogleMapZoneComponent = ({ zonaid, selectedLamp, selectedZoneId, filter }) => {
  const [mapInstance, setMapInstance] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [zona, setZona] = useState(null);
  const [stylesReady, setStylesReady] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [overlayPosition, setOverlayPosition] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY
  });

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

  const location = useLocation();

  useEffect(() => {
    if (!mapInstance || !selectedMarker) return;

    const listener = mapInstance.addListener("bounds_changed", () => {
      updateOverlayPosition(selectedMarker.lat, selectedMarker.lng);
    });

    return () => window.google.maps.event.removeListener(listener);
  }, [mapInstance, selectedMarker]);

  useEffect(() => {
    setSelectedMarker(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!selectedMarker) return;

    const stillExists = markers.find(
      m => m.devEUNumber === selectedMarker.devEUNumber
    );

    // ako više ne postoji ili ne prolazi filter → zatvori
    if (
      !stillExists ||
      (filter && filter !== "all" && stillExists.status !== filter)
    ) {
      setSelectedMarker(null);
    }

  }, [markers, filter]);

  useEffect(() => {
    requestAnimationFrame(() => setStylesReady(true));
  }, []);

  // 🔥 FIX InfoWindow
  useEffect(() => {
    if (!selectedMarker) return;

    const fixStyle = () => {
      const iw = document.querySelector('.gm-style-iw-c');
      if (iw) {
        iw.style.background = 'transparent';
        iw.style.boxShadow = 'none';
        iw.style.padding = '0px';
      }

      const tip = document.querySelector('.gm-style-iw-tc');
      if (tip) {
        tip.style.display = 'none';
      }
    };

    setTimeout(fixStyle, 0);
    setTimeout(fixStyle, 50);
    setTimeout(fixStyle, 150);

  }, [selectedMarker]);

  // MQTT
  useEffect(() => {
    const client = mqtt.connect('wss://smartlight.bhtelecom.ba:8083');

    client.on('connect', () => {
      client.subscribe('/smartmap/updates/#', { qos: 1 });
    });

    client.on('message', (topic, message) => {
      const data = JSON.parse(message.toString());

      setMarkers(prev => {
        const updated = prev.map(m =>
          m.devEUNumber === data.devEUNumber
            ? {
              ...m,
              status: data.status,
              currentDimming: data.bri ?? data.dimming ?? m.currentDimming
            }
            : m
        );

        setSelectedMarker(prevMarker => {
          if (!prevMarker) return prevMarker;

          if (prevMarker.devEUNumber === data.devEUNumber) {
            return {
              ...prevMarker,
              status: data.status,
              currentDimming: data.bri ?? data.dimming ?? prevMarker.currentDimming
            };
          }

          return prevMarker;
        });

        return updated;
      });
    });

    return () => client.end(true);
  }, []);

  useEffect(() => {
    if (zonaid) {
      fetchLamps(zonaid);
      getZona(zonaid);
    }
  }, [zonaid]);

  useEffect(() => {
    if (zona) {
      setMapCenter(JSON.parse(zona.centar));
    }
  }, [zona]);

  // locate
  useEffect(() => {
    if (selectedLamp && mapInstance && selectedZoneId === zonaid) {
      const lat = Number(selectedLamp.latitude);
      const lng = Number(selectedLamp.longitude);

      mapInstance.panTo({ lat, lng });
      mapInstance.setZoom(19);

      setSelectedMarker({
        lat,
        lng,
        devEUNumber: selectedLamp.devEUNumber,
        name: selectedLamp.name,
        status: selectedLamp.status,
        currentDimming: selectedLamp.currentDimming
      });
    }
  }, [selectedLamp, selectedZoneId, zonaid, mapInstance]);

  const updateOverlayPosition = (lat, lng) => {
    if (!mapInstance) return;

    const projection = mapInstance.getProjection();
    const bounds = mapInstance.getBounds();

    if (!projection || !bounds) return;

    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
    const point = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(lat, lng)
    );

    const scale = Math.pow(2, mapInstance.getZoom());

    const x = (point.x - bottomLeft.x) * scale;
    const y = (point.y - topRight.y) * scale;

    setOverlayPosition({ x, y });
  };

  const getZona = async (zonaid) => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/zonas/${zonaid}`);
      setZona(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLamps = async (zonaid) => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/streetlamps?zonaid=${zonaid}`);
      const parsed = response.data.map(l => ({
        lat: Number(l.latitude),
        lng: Number(l.longitude),
        status: l.status || "inactive",
        devEUNumber: l.devEUNumber,
        name: l.name,
        id: l.id,
        currentDimming: l.currentDimming
      }));
      setMarkers(parsed);
    } catch (err) {
      console.error(err);
    }
  };

  const getIconByType = (status) => {
    switch (status) {
      case "active":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lighton.png";
      case "inactive":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lightoff.png";
      case "alarm":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lightalarm.png";
      default:
        return "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
    }
  };

  const getIconByTypeSelected = (status) => {
    switch (status) {
      case "active":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lightonselected.png";
      case "inactive":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lightoffselected.png";
      case "alarm":
        return "https://smartlight-frontend-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/assets/icons/lightalarmselected.png";
      default:
        return "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
    }
  };

  if (!isLoaded || !mapCenter) return null;

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={mapCenter}
      zoom={19}
      mapTypeId="satellite"
      onLoad={map => setMapInstance(map)}
      onClick={() => setSelectedMarker(null)}
    >

      {/* 🔥 FILTER MARKERA */}
      {markers
        .filter(m => {
          if (!filter || filter === "all") return true;
          return m.status === filter;
        })
        .map((m, i) => {
          const isSelected = selectedMarker?.devEUNumber === m.devEUNumber;

          return (
            <Marker
              key={i}
              position={{ lat: m.lat, lng: m.lng }}
              label={{
                text: String(m.name),
                color: "#ff9800",
                fontSize: "12px",
                fontWeight: "bold",
              }}
              icon={{
                url: isSelected
                  ? getIconByTypeSelected(m.status)
                  : getIconByType(m.status),

                scaledSize: new window.google.maps.Size(
                  isSelected ? 60 : 40,
                  isSelected ? 60 : 40
                ),

                labelOrigin: new window.google.maps.Point(
                  isSelected ? 30 : 20,
                  isSelected ? -10 : -5
                )
              }}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                setSelectedMarker({ ...m });   // 🔥 BITNO - kopija
                updateOverlayPosition(m.lat, m.lng);
              }}
            />
          );
        })}

      {selectedMarker && overlayPosition && (
        <div
          style={{
            position: "absolute",
            left: overlayPosition.x,
            top: overlayPosition.y,
            transform: "translate(-50%, -140%)",
            zIndex: 999
          }}
        >
          <div style={{
            background: "rgba(0, 21, 54, 0.9)",
            backdropFilter: "blur(10px)",
            color: "#fff",
            padding: "10px",
            borderRadius: "12px",
            minWidth: "220px",
            boxShadow: "0 4px 25px rgba(0,0,0,0.5)"
          }}>
            <div style={{ color: "#ff9800", fontWeight: 600 }}>
              {selectedMarker.name}
            </div>

            <LampControl lamp={selectedMarker} />
          </div>
        </div>
      )}
    </GoogleMap>
  );
};

export default React.memo(GoogleMapZoneComponent);